import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
  Collapse,
  IconButton,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import * as StoryScript from '../services/StoryScriptService';
import VideoThumbnailPanel from '../components/VideoThumbnailPanel';
import AuthService from '../services/AuthService';
import VoiceOverConfigService from '../services/VoiceOverConfigService';
import { surface } from '../theme';
import MessageBubble from './sleep-videos-v3/MessageBubble';
import PipelineStepper from './sleep-videos-v3/PipelineStepper';
import VoiceOverSection from './sleep-videos-v3/VoiceOverSection';
import RenderSection from './sleep-videos-v3/RenderSection';
import ScriptSection from './sleep-videos-v3/ScriptSection';
import {
  ACTIVE_RENDER_STATUSES,
  POLL_INTERVAL,
  SCRIPT_STATUS_CHIP,
  VOICE_OVER_DEFAULT_BY_PROFILE,
} from './sleep-videos-v3/constants';
import { labelForAiStoriesProgressStep } from './ai-stories/constants';
import ChannelFootageNotice from '../components/ChannelFootageNotice';
import { formatEastern } from '../utils/easternTime';

function getAudioDuration(file) {
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.addEventListener('loadedmetadata', () => {
      resolve(audio.duration);
      URL.revokeObjectURL(audio.src);
    });
    audio.addEventListener('error', () => {
      resolve(null);
      URL.revokeObjectURL(audio.src);
    });
    audio.src = URL.createObjectURL(file);
  });
}

export default function StoryScriptDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = AuthService.hasRole(['admin', 'superadmin']);

  const [detail, setDetail] = useState(null);
  const [plots, setPlots] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPlots, setGeneratingPlots] = useState(false);
  const [selectingPlot, setSelectingPlot] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepNumber, setStepNumber] = useState(null);
  const [totalSteps, setTotalSteps] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [scriptCopied, setScriptCopied] = useState(false);
  const [showFullScript, setShowFullScript] = useState(false);
  const [messages, setMessages] = useState([]);
  const pollingRef = useRef(null);
  const plotsMessagesPollRef = useRef(null);
  const expectGenerationUpdatesRef = useRef(false);
  const isMountedRef = useRef(true);
  const currentScriptIdRef = useRef(id);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const voFileInputRef = useRef(null);
  const [rendering, setRendering] = useState(false);
  const [generatingVoiceOver, setGeneratingVoiceOver] = useState(false);
  const [generateVoiceOverError, setGenerateVoiceOverError] = useState(null);
  const [voiceOverConfigs, setVoiceOverConfigs] = useState([]);
  const [selectedVoiceOverConfigForGenerate, setSelectedVoiceOverConfigForGenerate] =
    useState(null);
  const [generateScriptError, setGenerateScriptError] = useState(null);
  const [editorPastedScript, setEditorPastedScript] = useState('');
  const [editorSavingScript, setEditorSavingScript] = useState(false);
  const [editorSaveScriptError, setEditorSaveScriptError] = useState(null);

  const clearPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const clearPlotsMessagesPolling = () => {
    if (plotsMessagesPollRef.current) {
      clearInterval(plotsMessagesPollRef.current);
      plotsMessagesPollRef.current = null;
    }
  };

  useEffect(() => {
    currentScriptIdRef.current = id;
  }, [id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadDoc = useCallback(async () => {
    if (!id) return;
    try {
      const data = await StoryScript.fetchScript(id);
      if (data.success && data.script) {
        setDetail(data.script);
        setMessages(data.script.messages ?? []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  const startPolling = useCallback(() => {
    clearPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const data = await StoryScript.fetchScript(id);
        if (data.success && data.script) {
          const s = data.script;
          setDetail(s);
          setMessages(s.messages ?? []);
          if (s.progress?.currentStep != null) {
            setCurrentStep(s.progress.currentStep);
          }
          if (typeof s.progress?.stepNumber === 'number') {
            setStepNumber(s.progress.stepNumber);
          }
          if (typeof s.progress?.totalSteps === 'number') {
            setTotalSteps(s.progress.totalSteps);
          }
          if (s.status === 'completed' && s.script) {
            setSelectingPlot(false);
            setCurrentStep(null);
            setStepNumber(null);
            setTotalSteps(null);
            clearPolling();
          } else if (
            !expectGenerationUpdatesRef.current &&
            s.status !== 'script_generating' &&
            s.status !== 'queued'
          ) {
            clearPolling();
          }
        }
      } catch (e) {
        console.error(e);
      }
    }, 2000);
  }, [id]);

  useEffect(() => {
    if (detail?.id && isAdmin) {
      VoiceOverConfigService.list('ai-stories')
        .then((list) => setVoiceOverConfigs(Array.isArray(list) ? list : []))
        .catch(() => setVoiceOverConfigs([]));
    }
  }, [detail?.id, isAdmin]);

  const prevDocIdRef = useRef(null);
  useEffect(() => {
    if (detail?.id !== prevDocIdRef.current) {
      prevDocIdRef.current = detail?.id ?? null;
      setSelectedVoiceOverConfigForGenerate(null);
      setShowFullScript(false);
      setEditorSaveScriptError(null);
    }
  }, [detail?.id]);

  useEffect(() => {
    setEditorPastedScript(detail?.script ?? '');
  }, [detail?.id]);

  useEffect(() => {
    if (
      !detail?.id ||
      !voiceOverConfigs.length ||
      detail.voiceOverConfigId ||
      selectedVoiceOverConfigForGenerate != null
    ) {
      return;
    }
    const channelVoiceConfigId = detail.channel?.voiceOverConfigId;
    if (channelVoiceConfigId) {
      const channelConfig = voiceOverConfigs.find((c) => c.id === channelVoiceConfigId);
      if (channelConfig) {
        setSelectedVoiceOverConfigForGenerate(channelConfig);
        return;
      }
    }
    const pn = detail.channel?.profileNumber;
    if (pn == null) return;
    const num = Number(pn);
    if (Number.isNaN(num)) return;
    let voiceId = null;
    if (num >= VOICE_OVER_DEFAULT_BY_PROFILE.low.min && num <= VOICE_OVER_DEFAULT_BY_PROFILE.low.max) {
      voiceId = VOICE_OVER_DEFAULT_BY_PROFILE.low.voice_id;
    } else if (
      num >= VOICE_OVER_DEFAULT_BY_PROFILE.high.min &&
      num <= VOICE_OVER_DEFAULT_BY_PROFILE.high.max
    ) {
      voiceId = VOICE_OVER_DEFAULT_BY_PROFILE.high.voice_id;
    }
    if (!voiceId) return;
    const config = voiceOverConfigs.find((c) => c.voice_id === voiceId);
    if (config) setSelectedVoiceOverConfigForGenerate(config);
  }, [
    detail?.id,
    detail?.voiceOverConfigId,
    detail?.channel?.voiceOverConfigId,
    detail?.channel?.profileNumber,
    voiceOverConfigs,
    selectedVoiceOverConfigForGenerate,
  ]);

  useEffect(() => {
    if (!detail?.voiceOverConfigId || !voiceOverConfigs.length || selectedVoiceOverConfigForGenerate != null) {
      return;
    }
    const config = voiceOverConfigs.find((c) => c.id == detail.voiceOverConfigId);
    if (config) setSelectedVoiceOverConfigForGenerate(config);
  }, [detail?.id, detail?.voiceOverConfigId, voiceOverConfigs, selectedVoiceOverConfigForGenerate]);

  useEffect(() => {
    if (!isAdmin || !id || !selectedVoiceOverConfigForGenerate?.id) return;
    if (detail?.voiceOverConfigId === selectedVoiceOverConfigForGenerate.id) return;
    let cancelled = false;
    StoryScript.updateScript(id, {
      voiceOverConfigId: selectedVoiceOverConfigForGenerate.id,
    })
      .then(() => {
        if (!cancelled) loadDoc();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    isAdmin,
    id,
    selectedVoiceOverConfigForGenerate?.id,
    detail?.voiceOverConfigId,
    loadDoc,
  ]);

  useEffect(() => {
    if (!isAdmin || !detail?.id) return undefined;
    const hasActiveVoice =
      detail.voiceOverGenerationStatus?.status === 'queued' ||
      detail.voiceOverGenerationStatus?.status === 'generating' ||
      detail.voiceOverGenerationStatus?.status === 'generating-voiceover';
    const hasActiveRender = ACTIVE_RENDER_STATUSES.includes(detail?.renderStatus?.status);
    const hasActiveUpload = ['queued', 'uploading', 'retrying'].includes(detail.youtube?.uploadStatus) ||
      (detail.autoUploadToChannel && !detail.youtube?.uploadStatus && detail.renderStatus?.status === 'completed');
    const hasActiveScriptGen =
      detail.status === 'queued' || detail.status === 'script_generating';
    if (!hasActiveVoice && !hasActiveRender && !hasActiveScriptGen && !hasActiveUpload) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    pollRef.current = setInterval(() => loadDoc(), POLL_INTERVAL);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [
    isAdmin,
    detail?.id,
    detail?.status,
    detail?.voiceOverGenerationStatus?.status,
    detail?.renderStatus?.status,
    detail?.youtube?.uploadStatus,
    detail?.autoUploadToChannel,
    loadDoc,
  ]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!id) {
        setError('Script ID required');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const data = await StoryScript.fetchScript(id);
        if (cancelled) return;
        if (data.success && data.script) {
          const s = data.script;
          setDetail(s);
          const sStatus = s.status || 'creating';
          setPlots(s.plots || null);
          setMessages(s.messages ?? []);
          if (s.progress?.currentStep != null) {
            setCurrentStep(s.progress.currentStep);
          }
          if (typeof s.progress?.stepNumber === 'number') {
            setStepNumber(s.progress.stepNumber);
          }
          if (typeof s.progress?.totalSteps === 'number') {
            setTotalSteps(s.progress.totalSteps);
          }
          if (!cancelled) setLoading(false);
          if (sStatus === 'queued' || sStatus === 'script_generating') {
            setSelectingPlot(true);
            startPolling();
          } else if (sStatus === 'script_failed') {
            setSelectingPlot(false);
            setCurrentStep(null);
            setStepNumber(null);
            setTotalSteps(null);
          } else if (sStatus === 'completed' && s.script) {
            setSelectingPlot(false);
            setCurrentStep(null);
            setStepNumber(null);
            setTotalSteps(null);
          } else if (sStatus === 'generating_plots') {
            setGeneratingPlots(true);
          }
        } else {
          setError(data.error || 'Failed to load script');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load script');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
      clearPolling();
      clearPlotsMessagesPolling();
      setGeneratingPlots(false);
    };
  }, [id, startPolling]);

  useEffect(() => {
    if (detail?.status !== 'generating_plots' || !id) return undefined;
    const poll = async () => {
      try {
        const data = await StoryScript.fetchScript(id);
        if (!isMountedRef.current || currentScriptIdRef.current !== id) return;
        if (data.success && data.script) {
          const s = data.script;
          setDetail(s);
          setMessages(s.messages ?? []);
          if (s.plots) setPlots(s.plots);
          if (s.progress?.currentStep != null) {
            setCurrentStep(s.progress.currentStep);
          } else {
            setCurrentStep(null);
          }
          if (typeof s.progress?.stepNumber === 'number') {
            setStepNumber(s.progress.stepNumber);
          }
          if (typeof s.progress?.totalSteps === 'number') {
            setTotalSteps(s.progress.totalSteps);
          }
          if (s.status !== 'generating_plots') {
            setGeneratingPlots(false);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    const t = setInterval(poll, 2000);
    void poll();
    return () => clearInterval(t);
  }, [detail?.status, id]);

  const status = detail?.status || '';
  const scriptTitle = detail?.novelTitle;
  const scriptBody = detail?.script || null;

  const pipelineDoc = useMemo(() => {
    if (!detail) return null;
    let genStatus = { status: 'idle', error: '' };
    if (detail.status === 'completed' && detail.script) {
      genStatus = { status: 'completed', error: '' };
    } else if (detail.status === 'generating_plots') {
      genStatus = { status: 'generating', error: '' };
    } else if (detail.status === 'script_generating') {
      genStatus = { status: 'generating', error: '' };
    } else if (detail.status === 'queued') {
      genStatus = { status: 'queued', error: '' };
    } else if (detail.status === 'script_failed') {
      genStatus = {
        status: 'failed',
        error: detail.scriptGenerationError || '',
      };
    }
    return {
      ...detail,
      fullScript: detail.script || '',
      generationStatus: genStatus,
    };
  }, [detail]);

  // Runs the full backend generation (plot when needed → script). Pass an
  // empty plot to have the backend generate one as the first step.
  const runSelectPlotGeneration = async (plot) => {
    if (!scriptTitle || !id) return;
    setError('');
    setGenerateScriptError(null);
    setSelectingPlot(true);
    setCurrentStep(plot ? 'choosing-plot' : 'plot');
    setStepNumber(null);
    setTotalSteps(null);
    expectGenerationUpdatesRef.current = true;
    startPolling();
    try {
      const result = await StoryScript.selectPlotAndGenerate(
        plot,
        scriptTitle,
        detail?.niche || 'bikers',
        id,
        (stepId, st, meta = {}) => {
          if (typeof meta.stepNumber === 'number') setStepNumber(meta.stepNumber);
          if (typeof meta.totalSteps === 'number') setTotalSteps(meta.totalSteps);
          if (st === 'start') setCurrentStep(stepId);
        },
      );
      if (result.success && result.script) {
        setSelectingPlot(false);
        setCurrentStep(null);
        setStepNumber(null);
        setTotalSteps(null);
        const refreshed = await StoryScript.fetchScript(id);
        if (refreshed.success && refreshed.script) {
          setDetail(refreshed.script);
          setMessages(refreshed.script.messages ?? []);
        }
      }
    } catch (err) {
      const msg = err.message || 'Generation failed';
      setGenerateScriptError(msg);
      setCurrentStep(null);
      setStepNumber(null);
      setTotalSteps(null);
      await loadDoc();
    } finally {
      expectGenerationUpdatesRef.current = false;
      clearPolling();
      setSelectingPlot(false);
    }
  };

  const handleGenerateScript = async () => {
    await runSelectPlotGeneration(
      String(detail?.selectedPlot ?? '').trim(),
    );
  };

  // A stored plot means script generation should already be underway — continue
  // it automatically (covers the post-plot handoff, reloads, and stale tabs).
  const autoContinuedForRef = useRef(null);
  useEffect(() => {
    if (!detail?.id || autoContinuedForRef.current === detail.id) return;
    const plot = String(detail?.selectedPlot ?? '').trim();
    if (
      detail.status !== 'plots_generated' ||
      !plot ||
      detail.script ||
      generatingPlots ||
      selectingPlot
    ) {
      return;
    }
    autoContinuedForRef.current = detail.id;
    void runSelectPlotGeneration(plot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    detail?.id,
    detail?.status,
    detail?.selectedPlot,
    detail?.script,
    generatingPlots,
    selectingPlot,
  ]);

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(
        `${scriptTitle || 'Untitled'}\n\n${scriptBody}`,
      );
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    } catch (e) {
      console.error(e);
      setError('Failed to copy to clipboard');
    }
  };

  const handleVoiceoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError(null);
    setSuccess(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const duration = await getAudioDuration(file);
      const { presignedUrl, objectName } = await StoryScript.requestVoiceoverPresignedUrl(id, {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'audio/mpeg',
      });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });
      await StoryScript.completeVoiceoverUpload(id, {
        objectName,
        fileSize: file.size,
        duration,
      });
      setSuccess('Voice over uploaded successfully');
      await loadDoc();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleGenerateVoiceOver = async () => {
    const configId = detail?.voiceOverConfigId || selectedVoiceOverConfigForGenerate?.id;
    if (!configId) return;
    if (!detail?.script?.trim()) return;
    try {
      setGeneratingVoiceOver(true);
      setGenerateVoiceOverError(null);
      await StoryScript.generateVoiceOver(id, {
        voiceOverConfigId: configId,
      });
      setSuccess('Voice over generation started');
      await loadDoc();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to generate voice over';
      setGenerateVoiceOverError(msg);
    } finally {
      setGeneratingVoiceOver(false);
    }
  };

  const handleRender = async () => {
    setError(null);
    setSuccess(null);
    setRendering(true);
    try {
      await StoryScript.renderScriptVideo(id);
      setSuccess('Render queued');
      await loadDoc();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to start render';
      setError(msg);
    } finally {
      setRendering(false);
    }
  };

  const handleBackgroundMusicChange = async (e) => {
    const value = e.target.checked;
    setError(null);
    setSuccess(null);
    try {
      await StoryScript.updateScript(id, { useBackgroundMusic: value });
      setDetail((prev) => (prev ? { ...prev, useBackgroundMusic: value } : null));
      setSuccess('Background music preference saved');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update preference');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  const busy =
    generatingPlots ||
    selectingPlot ||
    status === 'queued' ||
    status === 'script_generating' ||
    status === 'generating_plots';
  const stepLabel = currentStep ? labelForAiStoriesProgressStep(currentStep) : '';
  const hasScriptStepProgress =
    typeof totalSteps === 'number' &&
    totalSteps > 0 &&
    typeof stepNumber === 'number' &&
    stepNumber > 0;
  const progressPercent = hasScriptStepProgress
    ? Math.min(100, (stepNumber / totalSteps) * 100)
    : undefined;

  const renderActive = ACTIVE_RENDER_STATUSES.includes(detail?.renderStatus?.status);
  const footageRenderBlockedReason =
    detail?.voiceOverObjectName &&
    (!detail?.footageFolderIds || detail.footageFolderIds.length === 0)
      ? 'Configure a footage folder on the Channels page before rendering.'
      : null;
  const showAdminPipeline = isAdmin && detail && pipelineDoc;
  const showOuterBusyBar = showAdminPipeline
    ? generatingPlots ||
      (busy && !selectingPlot && status !== 'script_generating')
    : busy;

  const scriptGenIdleForGenerate =
    !generatingPlots &&
    !selectingPlot &&
    status !== 'queued' &&
    status !== 'script_generating' &&
    status !== 'generating_plots';
  const canGenerateScriptStep =
    (status === 'plots_generated' || status === 'script_failed') && scriptGenIdleForGenerate;
  const scriptChipForSection =
    SCRIPT_STATUS_CHIP[pipelineDoc?.generationStatus?.status] || SCRIPT_STATUS_CHIP.idle;
  const scriptSectionProgress = Math.round(hasScriptStepProgress ? progressPercent ?? 0 : 0);
  const scriptSectionIsGenerating =
    selectingPlot || status === 'queued' || status === 'script_generating';
  const scriptSectionIsCompleted = status === 'completed' && Boolean(scriptBody);
  const scriptSectionIsFailed = status === 'script_failed';
  const scriptSectionPasteVisible =
    status === 'plots_generated' ||
    status === 'script_failed' ||
    (status === 'completed' && Boolean(scriptBody)) ||
    ((status === 'creating' || status === '') && !scriptBody);

  const canShowGeneratePlotsCta =
    !plots &&
    !scriptBody &&
    (status === 'creating' || status === '' || status === 'generating_plots');

  const handleEditorSavePastedScript = async () => {
    if (!id) return;
    setEditorSaveScriptError(null);
    setEditorSavingScript(true);
    try {
      const trimmed = editorPastedScript.trim();
      await StoryScript.saveScriptPasted(id, trimmed);
      setEditorPastedScript(trimmed);
      await loadDoc();
    } catch (err) {
      setEditorSaveScriptError(
        err.response?.data?.error || err.message || 'Failed to save script',
      );
    } finally {
      setEditorSavingScript(false);
    }
  };

  const scriptWorkflowContent = (
    <>
      {showOuterBusyBar && (
        <Paper
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: surface.glass,
            boxShadow: 2,
          }}
        >
          <LinearProgress
            variant={hasScriptStepProgress ? 'determinate' : 'indeterminate'}
            value={progressPercent}
          />
          {hasScriptStepProgress && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Step {stepNumber} of {totalSteps}
            </Typography>
          )}
          {stepLabel && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mt: hasScriptStepProgress ? 0.5 : 1 }}
            >
              {stepLabel}
            </Typography>
          )}
        </Paper>
      )}

      {canShowGeneratePlotsCta && (
        <Paper
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: surface.glass,
            boxShadow: 2,
          }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Generate the story from the title and optional context you set when creating it — a plot
            is written first, then the full script — or paste your own script below and save without
            generating.
          </Typography>
          <Button
            variant='contained'
            size='medium'
            startIcon={
              selectingPlot || generatingPlots ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <PlayArrowIcon />
              )
            }
            onClick={handleGenerateScript}
            disabled={selectingPlot || generatingPlots || busy}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 2 }}
          >
            {selectingPlot || generatingPlots ? 'Generating…' : 'Generate story'}
          </Button>
        </Paper>
      )}

      {(messages.length > 0 || generatingPlots || status === 'generating_plots') &&
        !scriptSectionIsCompleted && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              px: 1,
              py: 1,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            {generatingPlots && messages.length === 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
                Connecting to the story model…
              </Typography>
            )}
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        </Box>
      )}

      {!isAdmin &&
        (status === 'plots_generated' ||
          status === 'script_failed' ||
          (status === 'completed' && scriptBody) ||
          ((status === 'creating' || status === '') && !scriptBody)) && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              background: surface.glass,
              boxShadow: 2,
            }}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
              Paste your own script
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              Or paste a script instead of generating from a plot. This marks the story as completed.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={14}
              placeholder='Paste or type your script here…'
              value={editorPastedScript}
              onChange={(e) => setEditorPastedScript(e.target.value)}
              disabled={busy}
              sx={{ mb: 1.5 }}
              slotProps={{
                input: {
                  sx: {
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    maxHeight: 320,
                    overflowY: 'auto',
                  },
                },
              }}
            />
            {editorSaveScriptError && (
              <Alert
                severity='error'
                sx={{ mb: 1.5 }}
                onClose={() => setEditorSaveScriptError(null)}
              >
                {editorSaveScriptError}
              </Alert>
            )}
            <Button
              variant='outlined'
              size='medium'
              startIcon={
                editorSavingScript ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />
              }
              onClick={handleEditorSavePastedScript}
              disabled={editorSavingScript || busy}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {editorSavingScript ? 'Saving…' : 'Save script'}
            </Button>
          </Paper>
        )}

      {!isAdmin &&
        status === 'script_failed' &&
        canGenerateScriptStep && (
          <Paper
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              background: surface.glass,
              boxShadow: 2,
            }}
          >
            <Alert severity='error' sx={{ mb: 2 }}>
              {detail?.scriptGenerationError || 'Script generation failed.'}
            </Alert>
            <Button
              variant='contained'
              size='medium'
              startIcon={
                selectingPlot ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <ReplayIcon />
                )
              }
              onClick={handleGenerateScript}
              disabled={busy}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 2 }}
            >
              {selectingPlot ? 'Starting…' : 'Retry'}
            </Button>
            {detail?.debugScreenshotUrl && (
              <Button
                variant='outlined'
                size='medium'
                startIcon={<OpenInNewIcon />}
                onClick={() =>
                  window.open(
                    detail.debugScreenshotUrl,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                disabled={busy}
                sx={{ ml: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                View Debug Screenshot
              </Button>
            )}
          </Paper>
        )}

      {!showAdminPipeline && scriptBody && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            background: surface.glass,
            boxShadow: 2,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowFullScript(!showFullScript)}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600, flex: 1 }}>
              Full Script
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mr: 1 }}>
              {(scriptBody || '').trim().split(/\s+/).filter(Boolean).length} words
            </Typography>
            <Button
              size='small'
              variant='outlined'
              startIcon={scriptCopied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              onClick={async (e) => {
                e.stopPropagation();
                await copyScript();
              }}
              sx={{ mr: 0.5 }}
            >
              {scriptCopied ? 'Copied!' : 'Copy'}
            </Button>
            <IconButton size='small' aria-label={showFullScript ? 'Collapse script' : 'Expand script'}>
              {showFullScript ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showFullScript}>
            <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 1 }}>
              <Typography
                variant='body2'
                component='div'
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {scriptBody}
              </Typography>
            </Box>
          </Collapse>
        </Paper>
      )}
    </>
  );

  return (
    <Box>
      <Box sx={{ px: { xs: 1, sm: 2 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/ai-stories')}
          sx={{ color: 'white', mb: 2, textTransform: 'none' }}
        >
          Back
        </Button>

        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            mb: 1,
          }}
        >
          {scriptTitle || 'Story'}
        </Typography>

        {detail?.context?.trim() && (
          <Typography
            variant='body1'
            sx={{
              color: 'rgba(255,255,255,0.88)',
              mb: 2,
              whiteSpace: 'pre-wrap',
              maxWidth: 900,
            }}
          >
            {detail.context.trim()}
          </Typography>
        )}

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {detail?.scheduledPublishAt && (
          <Alert severity='info' sx={{ mb: 2 }}>
            {detail.automated ? 'Automated video. ' : ''}
            Uploads to YouTube as a scheduled video that publishes {formatEastern(detail.scheduledPublishAt)}.
          </Alert>
        )}

        {showAdminPipeline ? (
          <>
            <PipelineStepper
              doc={pipelineDoc}
              uploadStatus={detail.youtube?.uploadStatus || (detail.autoUploadToChannel ? 'waiting' : 'off')}
              uploadProgress={detail.youtube?.progress || 0}
              videoTitle={scriptTitle || ''}
              videoContext={detail?.selectedPlot || ''}
              generating={selectingPlot}
              generatingVoiceOver={generatingVoiceOver}
              rendering={rendering}
              handleGenerate={handleGenerateScript}
              scriptRetryRequiresContext={false}
              handleGenerateVoiceOver={handleGenerateVoiceOver}
              handleRender={handleRender}
              hasVoiceOverConfig={!!(detail?.voiceOverConfigId || selectedVoiceOverConfigForGenerate?.id)}
              scriptDebugScreenshotUrl={detail?.debugScreenshotUrl}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                {scriptWorkflowContent}
                <ScriptSection
                  doc={pipelineDoc}
                  videoTitle={scriptTitle || ''}
                  videoContext={detail?.selectedPlot || ''}
                  generating={selectingPlot}
                  generateError={generateScriptError}
                  onDismissGenerateError={() => setGenerateScriptError(null)}
                  scriptChip={scriptChipForSection}
                  generationStatus={pipelineDoc.generationStatus}
                  progress={scriptSectionProgress}
                  stepLabel={stepLabel}
                  isGenerating={scriptSectionIsGenerating}
                  isCompleted={scriptSectionIsCompleted}
                  isFailed={scriptSectionIsFailed}
                  canGenerate={canGenerateScriptStep}
                  messages={[]}
                  showFullScript={showFullScript}
                  setShowFullScript={setShowFullScript}
                  copiedFullScript={scriptCopied}
                  setCopiedFullScript={setScriptCopied}
                  handleGenerate={handleGenerateScript}
                  messagesEndRef={messagesEndRef}
                  videoId={id}
                  onScriptSaved={loadDoc}
                  onSavePastedScript={(trimmed) => StoryScript.saveScriptPasted(id, trimmed)}
                  missingPrerequisitesMessage='Click Generate story first, or paste a script.'
                  generateScriptHint='Generate the full script — the plot is written automatically as the first step.'
                  showPasteSection={scriptSectionPasteVisible}
                />
                <ChannelFootageNotice channel={detail.channel} kind="ai-stories" folder={detail.footageFolders?.[0] || null} />
                <VideoThumbnailPanel key={id} kind='ai-stories' videoId={id} title={scriptTitle} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                <VoiceOverSection
                  doc={pipelineDoc}
                  voiceOverConfigs={voiceOverConfigs}
                  selectedVoiceOverConfigForGenerate={selectedVoiceOverConfigForGenerate}
                  setSelectedVoiceOverConfigForGenerate={setSelectedVoiceOverConfigForGenerate}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  generatingVoiceOver={generatingVoiceOver}
                  generateVoiceOverError={generateVoiceOverError}
                  onDismissGenerateVoiceOverError={() => setGenerateVoiceOverError(null)}
                  handleVoiceoverSelect={handleVoiceoverSelect}
                  handleGenerateVoiceOver={handleGenerateVoiceOver}
                  handleBackgroundMusicChange={handleBackgroundMusicChange}
                  voFileInputRef={voFileInputRef}
                />
                <RenderSection
                  doc={pipelineDoc}
                  rendering={rendering}
                  handleRender={handleRender}
                  renderActive={renderActive}
                  renderBlockedReason={footageRenderBlockedReason}
                />
              </Box>
            </Box>
          </>
        ) : (
          scriptWorkflowContent
        )}
      </Box>

    </Box>
  );
}
